import React, { useState, useEffect } from 'react';
import { Check, Star, Users, Search, Globe, Zap, Mail, AlertCircle, X, ChevronDown, ChevronUp } from 'lucide-react';

const PricingPage = () => {
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  
  // New state from second page
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [expandedFeatures, setExpandedFeatures] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

  // Check URL parameters and verify email on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const email = urlParams.get('email');

    if (token && email && !verificationAttempted) {
      setVerificationAttempted(true);
      verifyEmail(token, email);
    }
  }, [verificationAttempted]);

  // Email verification function
  const verifyEmail = async (token, email) => {
    setVerifyingEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/createuser/verifyemail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          email: email
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          setVerificationStatus('success');
          setUserEmail(email);
          
          if (data.data && data.data.sessionCreated) {
            setTimeout(async () => {
              try {
                const sessionResponse = await fetch(`${API_URL}/api/auth/verify-session`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  credentials: 'include',
                  body: JSON.stringify({
                    timestamp: Date.now(),
                    requestType: 'session_verification'
                  })
                });
                
                if (sessionResponse.ok) {
                  const sessionData = await sessionResponse.json();
                }
              } catch (error) {
                console.error('Error verifying session:', error);
              }
            }, 1000);
          }
        } else {
          if (data.data && data.data.alreadyVerified) {
            setVerificationStatus('success');
            setUserEmail(email);
          } else {
            setVerificationStatus('failed');
            setUserEmail(email);
          }
        }
      } else {
        setVerificationStatus('failed');
        setUserEmail(email);
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setVerificationStatus('failed');
      setUserEmail(email);
    } finally {
      setVerifyingEmail(false);
      setShowVerificationPopup(true);
      
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('token');
      newUrl.searchParams.delete('email');
      window.history.replaceState({}, '', newUrl);
    }
  };

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '£0',
      period: 'forever',
      description: 'Basic features to get started',
      popular: false,
      stripePrice: null,
      features: [
        { text: '1 Brand integration', included: true },
        { text: '1 Competitor tracking', included: true },
        { text: '1 Gap analysis', included: true },
        { text: '10 Contacts finder limit', included: true },
        { text: 'Keyword Explorer basic', included: true },
        { text: 'SERP Checker included', included: true },
        { text: 'Live Affiliate tracking', included: true }
      ]
    },
    {
      id: 'growth',
      name: 'Growth',
      price: '£199',
      period: '/month',
      description: 'Essential tools for growing teams',
      popular: false,
      stripePrice: process.env.REACT_APP_STRIPE_GROWTH_PRICE_ID || 'price_1ROgyCBQ0tqsgmxcRGL1uJ8w',
      features: [
        { text: '1 Brand integration', included: true },
        { text: '1 Competitor spy tracking', included: true },
        { text: '1 Gap analysis', included: true },
        { text: '100 Contacts finder limit', included: true },
        { text: 'Keyword Explorer full access', included: true },
        { text: 'SERP Checker unlimited', included: true },
        { text: 'Email Support included', included: true }
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '£399',
      period: '/month',
      description: 'Advanced features for professionals',
      popular: true,
      stripePrice: process.env.REACT_APP_STRIPE_PRO_PRICE_ID || 'price_1ROgyCBQ0tqsgmxcT5a2HFtX',
      features: [
        { text: '2 Brands integration', included: true },
        { text: '2 Competitors spy tracking', included: true },
        { text: '2 Gap analyses', included: true },
        { text: '500 Contacts finder limit', included: true },
        { text: 'All Tools full access', included: true },
        { text: 'Notifications real-time', included: true },
        { text: 'Email & Live Chat support', included: true }
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '£899',
      period: '/month',
      description: 'Everything for large organizations',
      popular: false,
      stripePrice: process.env.REACT_APP_STRIPE_ENTERPRISE_PRICE_ID || 'price_1ROgyCBQ0tqsgmxcXPKn78xE',
      features: [
        { text: '5 Brands integration', included: true },
        { text: '5 Competitors tracking', included: true },
        { text: '5 Gap analyses', included: true },
        { text: 'Unlimited contacts', included: true },
        { text: 'All Tools premium', included: true },
        { text: 'Priority Support 24/7', included: true },
        { text: 'Custom Features available', included: true }
      ]
    }
  ];

  const toggleFeatures = (planId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const isActivePlan = (planId) => {
    // This would come from your user context/state
    return false; // Placeholder
  };

  // Updated payment processing function to use payment_new endpoint
  const handlePlanSelection = async (plan) => {
    if (plan.name === 'Free') {
      window.location.href = '/dashboard';
      return;
    }

    setIsProcessing(true);
    setSelectedPlan(plan.id);
    setLoading(true);
    setLoadingPlan(plan.name);
    
    try {
      // First, fetch current billing data to get current plan
      const billingResponse = await fetch(`${API_URL}/api/billingPage`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      let currentPlan = {
        name: 'Free',
        planId: 'free',
        price: 0
      };

      if (billingResponse.ok) {
        const billingData = await billingResponse.json();
        if (billingData.currentPlan) {
          currentPlan = billingData.currentPlan;
        }
      }

      const response = await fetch(`${API_URL}/api/payment_new`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          planId: plan.id,
          priceId: plan.stripePrice,
          currentPlan: currentPlan,
          planName: plan.name,
          amount: parseFloat(plan.price.replace('£', '')) * 100, // Convert to cents for Stripe
          successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&plan=${encodeURIComponent(plan.name.toLowerCase())}`,
          cancelUrl: `${window.location.origin}/pricing`
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.type === 'first_checkout') {
          // Free plan user - redirect to Stripe checkout
          if (data.data && data.data.checkoutUrl) {
            // Store plan name for after redirect
            sessionStorage.setItem('upgradedPlanName', plan.name);
            window.location.href = data.data.checkoutUrl;
          } else {
            throw new Error('No checkout URL received');
          }
        } else if (data.type === 'upgrade') {
          // Show comparison modal for upgrades
          setComparisonData({
            ...data.data,
            selectedPlan: plan
          });
          setShowComparisonModal(true);
        } else if (data.type === 'downgrade') {
          // Handle downgrade scenario
          setErrorMessage('To downgrade your plan, please visit the billing page or contact support.');
          setShowErrorPopup(true);
        } else if (data.requiresUpgrade) {
          // Legacy response format
          setComparisonData({
            ...data,
            selectedPlan: plan
          });
          setShowComparisonModal(true);
        } else if (data.checkoutUrl) {
          // Direct checkout URL
          sessionStorage.setItem('upgradedPlanName', plan.name);
          window.location.href = data.checkoutUrl;
        } else if (data.url) {
          window.location.href = data.url;
        } else if (data.sessionId) {
          // Fallback to Stripe checkout if needed
          if (!window.Stripe) {
            const script = document.createElement('script');
            script.src = 'https://js.stripe.com/v3/';
            script.async = true;
            document.head.appendChild(script);
            
            await new Promise((resolve, reject) => {
              script.onload = resolve;
              script.onerror = () => reject(new Error('Failed to load Stripe.js'));
            });
          }

          const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_VeDSbq4Dz1tLirp5redn2ORD00iGjbNCy2';
          const stripe = window.Stripe(stripeKey);
          
          const { error } = await stripe.redirectToCheckout({ 
            sessionId: data.sessionId 
          });

          if (error) {
            throw new Error(`Stripe error: ${error.message}`);
          }
        } else {
          window.location.reload();
        }
      } else {
        setErrorMessage(data.message || 'Failed to process payment. Please try again.');
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error upgrading plan:', error);
      setErrorMessage('An error occurred while processing your request. Please try again.');
      setShowErrorPopup(true);
    } finally {
      setIsProcessing(false);
      setSelectedPlan(null);
      setLoading(false);
      setLoadingPlan(null);
    }
  };

  const VerificationPopup = () => {
    if (!showVerificationPopup) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative">
          <button
            onClick={() => setShowVerificationPopup(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center">
            {verificationStatus === 'success' ? (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Email Verified Successfully!</h3>
                <p className="text-gray-600 mb-6">
                  Your email <span className="font-medium text-green-600">{userEmail}</span> has been verified successfully. 
                  You can now choose your plan and get started.
                </p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Email Verification Failed</h3>
                <p className="text-gray-600 mb-6">
                  We couldn't verify your email <span className="font-medium text-red-600">{userEmail}</span>. 
                  The verification link may have expired or is invalid.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ErrorPopup = () => {
    if (!showErrorPopup) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative">
          <button
            onClick={() => setShowErrorPopup(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Payment Error</h3>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <button
              onClick={() => setShowErrorPopup(false)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  const LoadingVerification = () => {
    if (!verifyingEmail) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-blue-600 animate-pulse" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Verifying Email</h3>
          <p className="text-gray-600">Please wait while we verify your email address...</p>
          <div className="mt-4">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <LoadingVerification />
      <VerificationPopup />
      <ErrorPopup />
      
      <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>

        {/* Header */}
        <div className="mb-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <div className="p-2 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg">
                  <Zap className="w-6 h-6 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold mb-2 text-gray-800 dark:text-slate-100">
                Choose Your Plan
              </h1>
              <p className="text-lg text-gray-600 dark:text-slate-400 max-w-xl mx-auto mb-4">
                Unlock affiliate marketing intelligence with our tools
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-green-100 rounded-full border border-green-200">
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-green-700 font-medium text-sm">Secure payments powered by Stripe</span>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {plans.map((plan, index) => {
            const isActive = isActivePlan(plan.id);
            
            return (
              <div
                key={index}
                className={`bg-white dark:bg-slate-900 rounded-xl border shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] overflow-hidden relative flex flex-col ${
                  isActive
                    ? 'border-green-500 ring-2 ring-green-500 ring-opacity-20'
                    : plan.popular
                    ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-20'
                    : 'border-gray-200 dark:border-slate-700'
                }`}
                style={{ minHeight: '500px' }}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-2 py-1 text-xs font-medium rounded-bl-lg">
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Popular
                    </div>
                  </div>
                )}

                {isActive && (
                  <div className="absolute top-0 left-0 bg-gradient-to-r from-green-600 to-green-700 text-white px-2 py-1 text-xs font-medium rounded-br-lg">
                    <div className="flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Current
                    </div>
                  </div>
                )}

                <div className="p-4 flex flex-col h-full">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-1">{plan.name}</h3>
                    <div className="mb-2">
                      <span className="text-3xl font-bold text-gray-900 dark:text-slate-100">{plan.price}</span>
                      {plan.price !== '£0' && <span className="text-gray-600 dark:text-slate-400 text-sm">{plan.period}</span>}
                    </div>
                    <p className="text-gray-600 dark:text-slate-400 text-xs">{plan.description}</p>
                  </div>

                  <div className="space-y-2 mb-4 flex-grow">
                    {plan.features.slice(0, expandedFeatures[plan.id] ? plan.features.length : 7).map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-2">
                        <Check className={`w-3 h-3 ${feature.included ? 'text-green-500' : 'text-gray-300'} mt-0.5 flex-shrink-0`} />
                        <span className={`text-xs ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>{feature.text}</span>
                      </div>
                    ))}
                    
                    {plan.features.length > 7 && (
                      <button 
                        onClick={() => toggleFeatures(plan.id)}
                        className="flex items-center text-xs text-blue-600 hover:text-blue-700 font-medium mt-2 transition-colors"
                      >
                        {expandedFeatures[plan.id] ? (
                          <>
                            <ChevronUp className="w-3 h-3 mr-1" />
                            Show less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-3 h-3 mr-1" />
                            +{plan.features.length - 7} more
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      !isActive && handlePlanSelection(plan);
                    }}
                    disabled={isActive || (isProcessing && selectedPlan === plan.id)}
                    className={`w-full py-2 px-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 hover:shadow-lg text-sm disabled:transform-none disabled:cursor-not-allowed ${
                      isActive
                        ? 'bg-green-100 text-green-700 cursor-not-allowed border border-green-300'
                        : plan.popular
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-blue-600/30 hover:shadow-blue-600/40 disabled:from-gray-400 disabled:to-gray-500'
                        : 'bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 hover:shadow-md disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-400'
                    }`}
                  >
                    {isActive ? (
                      <>
                        <Check className="w-3 h-3" />
                        Active Plan
                      </>
                    ) : (isProcessing && selectedPlan === plan.id) || (loading && loadingPlan === plan.name) ? (
                      <>
                        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </>
                    ) : plan.name === 'Free' ? (
                      <>
                        <Users className="w-3 h-3" />
                        Go to Dashboard
                      </>
                    ) : (
                      <>
                        <Star className="w-3 h-3" />
                        Subscribe to {plan.name}
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Features Comparison */}
        <div className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">Why Choose Our Platform?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="p-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg w-fit mx-auto mb-2">
                  <Search className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">Advanced Analytics</h3>
                <p className="text-gray-600 text-xs">Deep insights into your affiliate performance</p>
              </div>
              <div className="text-center">
                <div className="p-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg w-fit mx-auto mb-2">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">Affiliate Discovery</h3>
                <p className="text-gray-600 text-xs">Find and recruit top affiliates in your niche</p>
              </div>
              <div className="text-center">
                <div className="p-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg w-fit mx-auto mb-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">Competitor Intelligence</h3>
                <p className="text-gray-600 text-xs">Stay ahead with competitor insights</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Full Width Footer */}
      <div className="w-full bg-gray-200 py-4 mt-8">
        <div className="text-center">
          <p className="text-xs text-gray-600">© 2026 Mars. All rights reserved.</p>
        </div>
      </div>
    </>
  );
};

export default PricingPage;